/**
 * CMS SITE endpoints (Site / Header / SiteTitle / Slider / Contact / About / Contract) —
 * see api/types/cmsSite.ts for the full per-entity confirmation notes (swagger + live curl).
 * Every route below is confirmed to exist in swagger; response field names confirmed via
 * live curl against `http://localhost:1538/` on 2026-08-10. Add/Edit/Delete responses are
 * assumed `ResponseBase`-only (no documented/observed payload beyond `hasError`), consistent
 * with this codebase's other CMS-adjacent CRUD endpoints — not independently re-verified
 * live because doing so risks the same runaway multi-hundred-MB nested-`Site` serialization
 * documented in api/types/cmsSite.ts's file header (Add/Edit both accept a full `Site`-shaped
 * payload and plausibly echo one back).
 */
import { apiPost } from '../client';
import type { ResponseBase } from '../types/common';
import type {
  About,
  AboutListResponse,
  CmsListRequest,
  Contract,
  ContractListResponse,
  Header,
  HeaderListResponse,
  Site,
  SiteContact,
  SiteContactListResponse,
  SiteListResponse,
  SiteTitle,
  SiteTitleListResponse,
  Slider,
  SliderListResponse,
} from '../types/cmsSite';

/* --------------------------------- Site ---------------------------------
 * Confirmed via swagger + live curl (see api/types/cmsSite.ts header) — request wraps the
 * entity under a `site` key, response field is `sites`. Only Site has a `GetSiteById` route.
 */

export function getSites(siteId?: number): Promise<SiteListResponse> {
  return apiPost<CmsListRequest, SiteListResponse>('Cms/Site/GetSites', { siteId });
}

export function getSiteById(id: number): Promise<SiteListResponse> {
  return apiPost<{ site: Partial<Site> }, SiteListResponse>('Cms/Site/GetSiteById', { site: { id } });
}

export function addSite(site: Omit<Site, 'id'>): Promise<ResponseBase> {
  return apiPost<{ site: Omit<Site, 'id'> }, ResponseBase>('Cms/Site/AddSite', { site });
}

export function editSite(site: Site): Promise<ResponseBase> {
  return apiPost<{ site: Site }, ResponseBase>('Cms/Site/EditSite', { site });
}

export function deleteSite(id: number): Promise<ResponseBase> {
  return apiPost<{ site: Partial<Site> }, ResponseBase>('Cms/Site/DeleteSite', { site: { id } });
}

/* --------------------------------- Header ---------------------------------
 * Confirmed via swagger + live curl (see api/types/cmsSite.ts header) — request wraps the
 * entity under a `header` key, response field is `headers`. NO `GetHeaderById` route exists.
 */

export function getHeaders(siteId?: number): Promise<HeaderListResponse> {
  return apiPost<CmsListRequest, HeaderListResponse>('Cms/Header/GetHeaders', { siteId });
}

export function addHeader(header: Omit<Header, 'id'>): Promise<ResponseBase> {
  return apiPost<{ header: Omit<Header, 'id'> }, ResponseBase>('Cms/Header/AddHeader', { header });
}

export function editHeader(header: Header): Promise<ResponseBase> {
  return apiPost<{ header: Header }, ResponseBase>('Cms/Header/EditHeader', { header });
}

export function deleteHeader(id: number): Promise<ResponseBase> {
  return apiPost<{ header: Partial<Header> }, ResponseBase>('Cms/Header/DeleteHeader', { header: { id } });
}

/* ------------------------------- SiteTitle -------------------------------
 * Confirmed via swagger + live curl (see api/types/cmsSite.ts header) — request wraps the
 * entity under a `siteTitle` key, response field is `siteTitles`.
 */

export function getSiteTitles(siteId?: number): Promise<SiteTitleListResponse> {
  return apiPost<CmsListRequest, SiteTitleListResponse>('Cms/SiteTitle/GetSiteTitles', { siteId });
}

export function addSiteTitle(siteTitle: Omit<SiteTitle, 'id'>): Promise<ResponseBase> {
  return apiPost<{ siteTitle: Omit<SiteTitle, 'id'> }, ResponseBase>('Cms/SiteTitle/AddSiteTitle', { siteTitle });
}

export function editSiteTitle(siteTitle: SiteTitle): Promise<ResponseBase> {
  return apiPost<{ siteTitle: SiteTitle }, ResponseBase>('Cms/SiteTitle/EditSiteTitle', { siteTitle });
}

export function deleteSiteTitle(id: number): Promise<ResponseBase> {
  return apiPost<{ siteTitle: Partial<SiteTitle> }, ResponseBase>('Cms/SiteTitle/DeleteSiteTitle', {
    siteTitle: { id },
  });
}

/* --------------------------------- Slider ---------------------------------
 * Confirmed via swagger + live curl (see api/types/cmsSite.ts header) — request wraps the
 * entity under a `slider` key, response field is `sliders`.
 */

export function getSliders(siteId?: number): Promise<SliderListResponse> {
  return apiPost<CmsListRequest, SliderListResponse>('Cms/Slider/GetSliders', { siteId });
}

export function addSlider(slider: Omit<Slider, 'id'>): Promise<ResponseBase> {
  return apiPost<{ slider: Omit<Slider, 'id'> }, ResponseBase>('Cms/Slider/AddSlider', { slider });
}

export function editSlider(slider: Slider): Promise<ResponseBase> {
  return apiPost<{ slider: Slider }, ResponseBase>('Cms/Slider/EditSlider', { slider });
}

export function deleteSlider(id: number): Promise<ResponseBase> {
  return apiPost<{ slider: Partial<Slider> }, ResponseBase>('Cms/Slider/DeleteSlider', { slider: { id } });
}

/* --------------------------------- Contact ---------------------------------
 * Confirmed via swagger + live curl (see api/types/cmsSite.ts header) — request wraps the
 * entity under a `contact` key (TS type is `SiteContact` to avoid a name clash with
 * api/types/common.ts's unrelated `Contact`), response field is `contacts`.
 */

export function getContacts(siteId?: number): Promise<SiteContactListResponse> {
  return apiPost<CmsListRequest, SiteContactListResponse>('Cms/Contact/GetContacts', { siteId });
}

export function addContact(contact: Omit<SiteContact, 'id'>): Promise<ResponseBase> {
  return apiPost<{ contact: Omit<SiteContact, 'id'> }, ResponseBase>('Cms/Contact/AddContact', { contact });
}

export function editContact(contact: SiteContact): Promise<ResponseBase> {
  return apiPost<{ contact: SiteContact }, ResponseBase>('Cms/Contact/EditContact', { contact });
}

export function deleteContact(id: number): Promise<ResponseBase> {
  return apiPost<{ contact: Partial<SiteContact> }, ResponseBase>('Cms/Contact/DeleteContact', { contact: { id } });
}

/* ---------------------------------- About ----------------------------------
 * Confirmed via swagger + live curl (see api/types/cmsSite.ts header) — request wraps the
 * entity under an `about` key, response field is `abouts`. The only one of these 7 with
 * directly-observed live row data (see api/types/cmsSite.ts's `About` doc comment).
 */

export function getAbouts(siteId?: number): Promise<AboutListResponse> {
  return apiPost<CmsListRequest, AboutListResponse>('Cms/About/GetAbouts', { siteId });
}

export function addAbout(about: Omit<About, 'id'>): Promise<ResponseBase> {
  return apiPost<{ about: Omit<About, 'id'> }, ResponseBase>('Cms/About/AddAbout', { about });
}

export function editAbout(about: About): Promise<ResponseBase> {
  return apiPost<{ about: About }, ResponseBase>('Cms/About/EditAbout', { about });
}

export function deleteAbout(id: number): Promise<ResponseBase> {
  return apiPost<{ about: Partial<About> }, ResponseBase>('Cms/About/DeleteAbout', { about: { id } });
}

/* --------------------------------- Contract ---------------------------------
 * Confirmed via swagger + live curl (see api/types/cmsSite.ts header) — request wraps the
 * entity under a `contract` key, response field is `contracts`.
 */

export function getContracts(siteId?: number): Promise<ContractListResponse> {
  return apiPost<CmsListRequest, ContractListResponse>('Cms/Contract/GetContracts', { siteId });
}

export function addContract(contract: Omit<Contract, 'id'>): Promise<ResponseBase> {
  return apiPost<{ contract: Omit<Contract, 'id'> }, ResponseBase>('Cms/Contract/AddContract', { contract });
}

export function editContract(contract: Contract): Promise<ResponseBase> {
  return apiPost<{ contract: Contract }, ResponseBase>('Cms/Contract/EditContract', { contract });
}

export function deleteContract(id: number): Promise<ResponseBase> {
  return apiPost<{ contract: Partial<Contract> }, ResponseBase>('Cms/Contract/DeleteContract', {
    contract: { id },
  });
}
